import { paletteLabel } from './palette'
import { metersFromFeet } from './format'
import type { DisplayUnits, ScenarioObject } from './types'
import {
  BRWN_PLUME_TYPE,
  brwnPlumeBlurb,
  formatBrwnPlumeDescription,
  isBrwnPlumeType,
  isGeneratedBrwnPlumeDescription,
} from './eggs/brwnPlume'

/**
 * Per-type Inspect defaults — blurbs, which controls appear, pose-fold naming.
 * Keep blurbs to ~2 short lines (what the object is / does on the board).
 */
export type ObjectTypeMeta = {
  /** Short function line under the object title. */
  blurb: string
  /**
   * Default timeline / story-beat copy when Description is empty.
   * Prefer short present-tense lines; keep author Description as the override.
   */
  descriptionTemplate: string
  /** Show Size sm/md/lg (icon weight). Plume hazards omit this — footprint owns visual weight. */
  hasIconSize: boolean
  /** Show / Hide window (two clocks). Surface wind and drawings omit. */
  hasVisibilityWindow: boolean
  /** Pose keyframe accordion title. */
  poseFoldLabel: 'Waypoints' | 'Position'
}

const BLURBS: Record<string, string> = {
  fire: 'Ignition or active fire on the board. Isolation distance marks the keep-out ring.',
  explosion: 'Blast or detonation marker. Blast radius is the keep-out ring around the origin.',
  gas_leak: 'Release origin for a gas leak. Footprint is the local keep-out ring; optional plume follows board wind.',
  rupture: 'Pipe or vessel rupture origin with an isolation keep-out ring.',
  vapor_cloud: 'Vapor hazard origin. Footprint is the local keep-out ring; optional plume follows board wind.',
  release_liquids: 'Liquid release origin on the board.',
  tornado: 'A tornado with its own wind.',
  hurricane: 'A hurricane with sustained wind of its own.',
  exclusion: 'Release or exclusion footprint — a keep-out area on the board.',
  leak: 'Legacy leak origin (same story as Gas Leak).',
  pipeburst: 'Legacy pipeburst — isolation keep-out around the origin.',
  hot_zone: 'Hot-zone keep-out ring for operations planning.',
  isolation: 'Isolation area keep-out ring.',
  evacuate: 'Evacuation zone radius on the board.',
  shelter: 'Shelter-in-place zone radius on the board.',
  icp: 'Incident command post location for the response.',
  dispatch: 'Dispatch / communications point for the response.',
  pipeline_personnel: 'Pipeline company personnel on scene.',
  responder: 'Responder on scene (not tied to a specific apparatus).',
  incident_commander: 'Incident commander location.',
  chief_officer: 'Chief officer location.',
  staging: 'Staging area for apparatus and crews.',
  media_staging: 'Media staging / joint information area.',
  muster: 'Muster or accountability point.',
  access_point: 'Access point into the incident area.',
  contractor: 'Contractor personnel on scene.',
  operator: 'Pipeline operator on scene.',
  person: 'Person on scene.',
  child: 'Child on scene.',
  car: 'Civilian or light vehicle that can move on waypoints.',
  truck: 'Heavy truck; can hold when a related road closes.',
  engine: 'Engine company apparatus; route with waypoints.',
  ladder: 'Truck / ladder company apparatus; route with waypoints.',
  ems: 'EMS / ambulance; route with waypoints.',
  hazmat: 'HAZMAT unit; route with waypoints.',
  rescue: 'Rescue apparatus; route with waypoints.',
  rehab: 'Rehab unit; route with waypoints.',
  police: 'Law enforcement unit; route with waypoints.',
  work_vehicle: 'Utility or work vehicle; route with waypoints.',
  bulldozer: 'Bulldozer; route with waypoints.',
  forestry_bulldozer: 'Forestry bulldozer; route with waypoints.',
  backhoe: 'Backhoe; route with waypoints.',
  boat: 'Boat; route with waypoints.',
  airplane: 'Airplane; route with waypoints.',
  helicopter: 'Helicopter; route with waypoints.',
  drone: 'Drone; route with waypoints.',
  rollover_vehicle: 'Rollover vehicle; route with waypoints.',
  contractor_response: 'Contractor vehicle; route with waypoints.',
  air_monitoring: 'Air monitoring equipment or team location.',
  hard_boom: 'Hard boom deployment location.',
  absorbent_boom: 'Absorbent boom deployment location.',
  boom: 'Boom deployment location.',
  foam: 'Foam supply or application point.',
  absorbent_pads: 'Absorbent pads cache or deployment.',
  underflow_dam: 'Underflow dam location.',
  overflow_dam: 'Overflow dam location.',
  dike: 'Dike or berm location.',
  temporary_mats: 'Temporary mats / ground protection.',
  restroom: 'Restrooms.',
  porta_potty: 'Porta potty.',
  frac_tank: 'Frac tank location.',
  vacuum_tanker: 'Tanker location.',
  vac_trucks: 'Vacuum truck location.',
  valve_open: 'Valve shown open.',
  valve_closed: 'Valve shown closed.',
  pump: 'Pump location.',
  compressor: 'Compressor location.',
  regulator_station: 'Regulator station.',
  city_gate: 'City gate station.',
  mercaptan_station: 'Mercaptan station.',
  meter_station: 'Meter station.',
  school: 'School location.',
  daycare: 'Daycare location.',
  hospital: 'Hospital location.',
  water_treatment: 'Water treatment facility.',
  road: 'Road segment with open / slow / closed status as time advances.',
  drawbridge: 'Drawbridge; deck can follow a related road closure.',
  wind: 'Board-wide Surface wind: direction, speed, and plume length/spread for leak and vapor origins.',
  label: 'Free text label on the map.',
  sketch: 'Line or freehand sketch. Drag the stroke to move it. The description is the map tooltip.',
  canal: 'Canal / waterway geometry.',
}

/** Timeline card fallbacks when Description is empty — short story beats, not Inspect blurbs. */
const DESCRIPTION_TEMPLATES: Record<string, string> = {
  fire: 'Fire reported on the map.',
  explosion: 'Explosion marked on the map.',
  gas_leak: 'Gas leak origin on the map.',
  rupture: 'Rupture / release origin with an isolation keep-out.',
  vapor_cloud: 'Vapor cloud origin on the map.',
  release_liquids: 'Liquid release on the map.',
  tornado: 'Tornado on the map.',
  hurricane: 'Hurricane on the map.',
  exclusion: 'Release / exclusion area marked on the map.',
  leak: 'Gas leak origin on the map.',
  pipeburst: 'Pipeburst origin with an isolation keep-out.',
  hot_zone: 'Hot zone keep-out marked on the map.',
  isolation: 'Isolation area marked on the map.',
  evacuate: 'Evacuation zone marked on the map.',
  shelter: 'Shelter-in-place zone marked on the map.',
  icp: 'Incident command post on the map.',
  dispatch: 'Dispatch on the map.',
  pipeline_personnel: 'Pipeline personnel on scene.',
  responder: 'Responder on scene.',
  incident_commander: 'Incident commander on scene.',
  chief_officer: 'Chief officer on scene.',
  staging: 'Staging area on the map.',
  media_staging: 'Media staging area on the map.',
  muster: 'Muster point on the map.',
  access_point: 'Access point marked on the map.',
  contractor: 'Contractor personnel on scene.',
  operator: 'Pipeline operator on scene.',
  person: 'Person on scene.',
  child: 'Child on scene.',
  car: 'Vehicle on the map.',
  truck: 'Truck on the map.',
  engine: 'Engine company on the map.',
  ladder: 'Ladder company on the map.',
  ems: 'EMS unit on the map.',
  hazmat: 'HAZMAT unit on the map.',
  rescue: 'Rescue unit on the map.',
  rehab: 'Rehab unit on the map.',
  police: 'Law enforcement unit on the map.',
  work_vehicle: 'Work vehicle on the map.',
  bulldozer: 'Bulldozer on the map.',
  forestry_bulldozer: 'Forestry bulldozer on the map.',
  backhoe: 'Backhoe on the map.',
  boat: 'Boat on the map.',
  airplane: 'Airplane on the map.',
  helicopter: 'Helicopter on the map.',
  drone: 'Drone on the map.',
  rollover_vehicle: 'Rollover vehicle on the map.',
  contractor_response: 'Contractor vehicle on the map.',
  air_monitoring: 'Air monitoring on the map.',
  hard_boom: 'Hard boom deployment on the map.',
  absorbent_boom: 'Absorbent boom on the map.',
  boom: 'Boom on the map.',
  foam: 'Foam supply / application on the map.',
  absorbent_pads: 'Absorbent pads on the map.',
  underflow_dam: 'Underflow dam on the map.',
  overflow_dam: 'Overflow dam on the map.',
  dike: 'Dike / berm on the map.',
  temporary_mats: 'Temporary mats on the map.',
  restroom: 'Restrooms on the map.',
  porta_potty: 'Porta potty on the map.',
  frac_tank: 'Frac tank on the map.',
  vacuum_tanker: 'Tanker on the map.',
  vac_trucks: 'Vacuum truck on the map.',
  valve_open: 'Valve shown open.',
  valve_closed: 'Valve shown closed.',
  pump: 'Pump on the map.',
  compressor: 'Compressor on the map.',
  regulator_station: 'Regulator station on the map.',
  city_gate: 'City gate on the map.',
  mercaptan_station: 'Mercaptan station on the map.',
  meter_station: 'Meter station on the map.',
  school: 'School on the map.',
  daycare: 'Daycare on the map.',
  hospital: 'Hospital on the map.',
  water_treatment: 'Water treatment facility on the map.',
  road: 'Temporary road status on the map.',
  drawbridge: 'Drawbridge on the map.',
  wind: 'Surface wind for the map.',
  label: 'Label on the map.',
  sketch: 'Sketch on the map.',
  canal: 'Canal / waterway on the map.',
}

/** Types that omit Size — footprint / zone slider (or no icon weight) is enough. */
const NO_ICON_SIZE = new Set([
  'gas_leak',
  'vapor_cloud',
  'leak',
  BRWN_PLUME_TYPE,
  'tornado',
  'hurricane',
  'wind',
  'sketch',
  'label',
])

const NO_VISIBILITY_WINDOW = new Set(['wind', 'label'])

const POSE_FOLD: Partial<Record<string, 'Waypoints' | 'Position'>> = {
  tornado: 'Position',
  hurricane: 'Position',
}

export function objectTypeMeta(typeId: string): ObjectTypeMeta {
  const label = paletteLabel(typeId)
  if (isBrwnPlumeType(typeId)) {
    return {
      blurb: brwnPlumeBlurb(),
      descriptionTemplate: '',
      hasIconSize: false,
      hasVisibilityWindow: true,
      poseFoldLabel: 'Waypoints',
    }
  }
  return {
    blurb: BLURBS[typeId] ?? `${label} on the board.`,
    descriptionTemplate: DESCRIPTION_TEMPLATES[typeId] ?? `${label} on the map.`,
    hasIconSize: !NO_ICON_SIZE.has(typeId),
    hasVisibilityWindow: !NO_VISIBILITY_WINDOW.has(typeId),
    poseFoldLabel: POSE_FOLD[typeId] ?? 'Waypoints',
  }
}

/**
 * Timeline / story-beat fallback when Description is empty.
 * Lightly adapts from object state (e.g. tornado with vs without a path).
 */
export function defaultObjectDescription(
  obj: Pick<ScenarioObject, 'type' | 'label' | 'keyframes' | 'params' | 'description'>,
  opts?: { firstName?: string; displayUnits?: DisplayUnits },
): string {
  const meta = objectTypeMeta(obj.type)
  const name = obj.label.trim() || paletteLabel(obj.type)
  if (isBrwnPlumeType(obj.type)) {
    const authored = obj.description?.trim()
    // Keep a custom Description; regenerate the auto story when units/radius change.
    if (authored && !isGeneratedBrwnPlumeDescription(authored)) return authored
    // Slider / preset value (params.radiusM) — not last keyframe — so the joke tracks what you just set.
    const radiusM = Number(obj.params.radiusM) || metersFromFeet(500)
    return formatBrwnPlumeDescription({
      firstName: opts?.firstName ?? '',
      radiusM,
      displayUnits: opts?.displayUnits ?? 'imperial',
    })
  }
  if (obj.type === 'tornado') {
    if ((obj.keyframes?.length ?? 0) >= 2) {
      return `${name} tracks across the area with local wind along the path.`
    }
    return `${name} marks a tornado location on the map.`
  }
  if (obj.type === 'hurricane') {
    if ((obj.keyframes?.length ?? 0) >= 2) {
      return `${name} tracks across the area. Sustained wind shifts as the center passes.`
    }
    return `${name} marks hurricane conditions on the map.`
  }
  if (obj.type === 'road') {
    const status = String(obj.params.status ?? 'open')
    return `${name} is ${status} on the map.`
  }
  if (
    (obj.type === 'engine' ||
      obj.type === 'ladder' ||
      obj.type === 'ems' ||
      obj.type === 'hazmat' ||
      obj.type === 'rescue' ||
      obj.type === 'police' ||
      obj.type === 'truck' ||
      obj.type === 'car' ||
      obj.type === 'airplane' ||
      obj.type === 'helicopter' ||
      obj.type === 'drone' ||
      obj.type === 'rollover_vehicle') &&
    (obj.keyframes?.length ?? 0) >= 2
  ) {
    return `${name} is en route on the map.`
  }
  const tmpl = meta.descriptionTemplate
  const typeLabel = paletteLabel(obj.type)
  if (tmpl.startsWith(typeLabel)) return `${name}${tmpl.slice(typeLabel.length)}`
  return tmpl
}

export function hasIconSize(typeId: string): boolean {
  return objectTypeMeta(typeId).hasIconSize
}

export function hasVisibilityWindow(typeId: string): boolean {
  return objectTypeMeta(typeId).hasVisibilityWindow
}

export function poseFoldLabel(typeId: string): 'Waypoints' | 'Position' {
  return objectTypeMeta(typeId).poseFoldLabel
}
